/**
 * @file UsersDashboard.js
 * @version 2.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-13
 * @modified 2026-10-12
 *
 * 2.1.0 (2026-10-12): a Xero column (where each person's own work reaches
 * Xero: Custom Connection / Own Xero login (connected or not) / Blocked (rep)),
 * and the Xero connection saved from the access window — last, after the
 * roles it depends on (PUT /api/users/admin/users/:id/xero-connection).
 *
 * User Management: the Users tab (accounts, their badge, roles and rep link) and
 * the Roles tab (the named permission groups those roles come from).
 *
 * The access model this drives, briefly: a role is a group of permissions and
 * carries a badge; a user holds any number of roles and their access is the
 * union, adjusted by per-user exceptions. See services/accessService.js.
 */

import { useCallback, useEffect, useState } from 'react';
import './TableStyles.css';
import './UsersDashboard.css';
import LoadingSpinner from './LoadingSpinner';
import { showAlert } from './Alert';
import ConfirmDialog from './ConfirmDialog';
import RolesManager from './RolesManager';
import RoleEditorModal from './RoleEditorModal';
import UserAccessModal from './UserAccessModal';

const BADGE_LABEL = { ADMIN: 'Admin', REP: 'Rep', USER: 'User' };

function UsersDashboard({ activeTab = 'users' }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [reps, setReps] = useState([]);
  // Xero connections overall: system mode, whether the Custom Connection is
  // configured, its allowance, who may change a person's (admins).
  const [xeroSummary, setXeroSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, title: '', message: '', type: 'warning', confirmText: 'Confirm', onConfirm: null
  });

  const [accessUser, setAccessUser] = useState(null);   // user being edited, or null
  const [savingAccess, setSavingAccess] = useState(false);
  const [roleEditor, setRoleEditor] = useState({ open: false, role: null });
  const [savingRole, setSavingRole] = useState(false);
  const [busyRoleId, setBusyRoleId] = useState(null);

  // ── Loading ───────────────────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    try {
      const [uRes, rRes, pRes, repRes] = await Promise.all([
        fetch('/api/users/admin/users', { credentials: 'include' }),
        fetch('/api/users/admin/roles', { credentials: 'include' }),
        fetch('/api/users/admin/permissions', { credentials: 'include' }),
        fetch('/api/users/admin/sales-reps', { credentials: 'include' })
      ]);

      if (uRes.ok) {
        const data = await uRes.json();
        setUsers(data.users || []);
        setXeroSummary(data.xero || null);
      } else showAlert('Failed to load users', 'error');

      if (rRes.ok) setRoles((await rRes.json()).roles || []);
      if (pRes.ok) {
        const data = await pRes.json();
        setCategories(data.categories || []);
        setPermissions(data.permissions || []);
      }
      if (repRes.ok) setReps((await repRes.json()).reps || []);
    } catch (error) {
      console.error('Error loading user management data:', error);
      showAlert('Connection error. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const closeConfirmDialog = () => setConfirmDialog(prev => ({ ...prev, isOpen: false }));

  // ── User access ───────────────────────────────────────────────────────────

  /**
   * Save the parts of a user's access in the order the server enforces: the
   * rep link first (a Rep role is refused without one), then the roles, then
   * the exceptions, then the Xero connection (refused for a Rep badge, so it
   * is judged on the roles just saved). Each call is skipped when nothing
   * about it changed, so editing only the exceptions does not rewrite the role
   * assignment and fill the audit log with no-ops.
   */
  const saveAccess = async ({ roleIds, repId, overrides, xeroConnection }) => {
    if (!accessUser) return;
    setSavingAccess(true);
    try {
      const currentRoleIds = (accessUser.roles || []).map(r => r.id).sort();
      const nextRoleIds = [...roleIds].sort();
      const rolesChanged = JSON.stringify(currentRoleIds) !== JSON.stringify(nextRoleIds);
      const repChanged = (accessUser.rep_id || null) !== (repId || null);
      const overridesChanged =
        JSON.stringify(accessUser.overrides || {}) !== JSON.stringify(overrides || {});
      const xeroChanged = !!xeroConnection
        && (accessUser.xero_connection || 'default') !== xeroConnection;

      const steps = [];
      if (repChanged) steps.push(['rep', { repId: repId ?? null }, 'rep']);
      if (rolesChanged) steps.push(['roles', { roleIds }, 'roles']);
      if (overridesChanged) steps.push(['permissions', { overrides }, 'permissions']);
      if (xeroChanged) steps.push(['xero-connection', { connection: xeroConnection }, 'Xero connection']);

      if (!steps.length) {
        setAccessUser(null);
        return;
      }

      for (const [path, body] of steps) {
        const response = await fetch(`/api/users/admin/users/${accessUser.id}/${path}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          showAlert(data.error || 'Failed to update access', 'error');
          return;   // Stop at the first failure — the later steps may depend on it.
        }
      }

      showAlert('Access updated', 'success');
      setAccessUser(null);
      fetchAll();
    } catch (error) {
      console.error('Error saving access:', error);
      showAlert('Connection error. Please try again.', 'error');
    } finally {
      setSavingAccess(false);
    }
  };

  // ── Roles ─────────────────────────────────────────────────────────────────

  const saveRole = async (form) => {
    setSavingRole(true);
    try {
      const editing = roleEditor.role;
      const response = await fetch(
        editing ? `/api/users/admin/roles/${editing.id}` : '/api/users/admin/roles',
        {
          method: editing ? 'PUT' : 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: form.name,
            description: form.description,
            badge: form.badge,
            requiresXero: form.requiresXero,
            permissions: form.permissions
          })
        }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        showAlert(editing ? 'Role updated' : 'Role created', 'success');
        setRoleEditor({ open: false, role: null });
        fetchAll();
      } else {
        showAlert(data.error || 'Failed to save role', 'error');
      }
    } catch (error) {
      console.error('Error saving role:', error);
      showAlert('Connection error. Please try again.', 'error');
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRole = (role) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Role',
      message: `Delete the role “${role.name}”? Anyone who held it keeps their per-user exceptions but loses everything this role granted.`,
      type: 'error',
      confirmText: 'Delete',
      onConfirm: () => executeDeleteRole(role)
    });
  };

  const executeDeleteRole = async (role) => {
    closeConfirmDialog();
    setBusyRoleId(role.id);
    try {
      const response = await fetch(`/api/users/admin/roles/${role.id}`, {
        method: 'DELETE', credentials: 'include'
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        showAlert(data.message || 'Role deleted', 'success');
        fetchAll();
      } else {
        showAlert(data.error || 'Failed to delete role', 'error');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      showAlert('Connection error. Please try again.', 'error');
    } finally {
      setBusyRoleId(null);
    }
  };

  /**
   * Email a user a link to set a new password.
   *
   * Confirmed first: it is an email to a real person and an admin misreading a
   * row should cost a dialog rather than somebody's confusion. The link always
   * goes to the address on the account — the request carries no destination, so
   * this cannot be used to redirect a reset.
   */
  const handleSendReset = (userId, userEmail) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Send password reset',
      message: `Email ${userEmail} a link to set a new password? `
        + 'The link lasts one hour and their current password keeps working until they use it.',
      confirmText: 'Send link',
      onConfirm: () => executeAction(
        userId, 'sending', `/api/users/${userId}/send-password-reset`, 'POST')
    });
  };

  // ── User lifecycle (approve / archive / delete) ───────────────────────────

  const handleApprove = (userId, userEmail) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Approve User',
      message: `Approve ${userEmail}? They will be given the Standard User role and emailed a link to set their password.`,
      type: 'info',
      confirmText: 'Approve',
      onConfirm: () => executeAction(userId, 'approving', `/api/users/admin/users/${userId}/approve`, 'POST')
    });
  };

  const handleDelete = (userId, userEmail) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete User',
      message: `Delete user ${userEmail}? This action cannot be undone.`,
      type: 'error',
      confirmText: 'Delete',
      onConfirm: () => executeAction(userId, 'deleting', `/api/users/admin/users/${userId}`, 'DELETE')
    });
  };

  const handleArchive = (userId, userEmail, archived) => {
    setConfirmDialog({
      isOpen: true,
      title: archived ? 'Archive User' : 'Unarchive User',
      message: archived
        ? `Archive user ${userEmail}? They will no longer be able to log in, but their history is kept.`
        : `Unarchive user ${userEmail}? They will be able to access the system again.`,
      type: archived ? 'warning' : 'info',
      confirmText: archived ? 'Archive' : 'Unarchive',
      onConfirm: () => executeAction(
        userId, archived ? 'archiving' : 'unarchiving',
        `/api/users/admin/users/${userId}/archive`, 'PATCH', { archived }
      )
    });
  };

  const executeAction = async (userId, label, url, method, body) => {
    closeConfirmDialog();
    setActionLoading(prev => ({ ...prev, [userId]: label }));
    try {
      const response = await fetch(url, {
        method,
        credentials: 'include',
        ...(body ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {})
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        showAlert(data.message || 'Done', 'success');
        fetchAll();
      } else {
        showAlert(data.error || 'Action failed', 'error');
      }
    } catch (error) {
      console.error('User action error:', error);
      showAlert('Connection error. Please try again.', 'error');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const pendingUsers = users.filter(u => u.pending_approval);
  const activeUsers = users.filter(u => !u.pending_approval && !u.is_archived);
  const archivedUsers = users.filter(u => !u.pending_approval && u.is_archived);

  if (loading) {
    return <div className="users-dashboard"><LoadingSpinner /></div>;
  }

  /** The badge chip plus the roles behind it — the same cell in both tables. */
  const renderAccessCell = (user) => (
    <>
      <div className="user-access-cell">
        <span className={`status-badge badge-${(user.badge || 'USER').toLowerCase()}`}>
          {BADGE_LABEL[user.badge] || 'User'}
        </span>
        <span className="role-chips">
          {(user.roles || []).length === 0
            ? <span className="muted">No roles</span>
            : user.roles.map(r => <span className="role-chip" key={r.id}>{r.name}</span>)}
          {Object.keys(user.overrides || {}).length > 0 && (
            <span
              className="role-chip exception"
              title={`${Object.keys(user.overrides).length} permission exception(s) set for this user`}
            >
              +exceptions
            </span>
          )}
        </span>
      </div>
    </>
  );

  /**
   * Where this person's own work reaches Xero, as the server works it out from
   * the system mode and their Xero connection. "(set)" marks a choice made for
   * them rather than the system default.
   */
  const renderXeroCell = (user) => {
    const route = user.xero_route || 'user';
    const icon = route === 'custom' ? 'hub' : route === 'blocked' ? 'block' : 'person';
    const set = user.xero_connection && user.xero_connection !== 'default';
    return (
      <span
        className={`xero-route-chip route-${route}${route === 'user' && user.xero_login && !user.xero_login.connected ? ' not-connected' : ''}`}
        title={set
          ? 'Set for this person on their access window — not the system default'
          : 'Follows the system’s Xero setting'}
      >
        <span className="material-symbols-rounded">{icon}</span>
        {user.xero_route_label || '—'}
        {set && <span className="xero-route-set">set</span>}
      </span>
    );
  };

  /** Actions render in the same slots on every row; inapplicable ones disable. */
  const renderUserActions = (user, { archived }) => (
    <td className="actions-col">
      <button
        className="btn-icon"
        onClick={() => setAccessUser(user)}
        disabled={!!actionLoading[user.id]}
        title="Manage roles, rep link, permission exceptions and Xero connection"
      >
        <span className="material-symbols-rounded">key</span>
      </button>
      <button
        className="btn-icon"
        onClick={() => handleArchive(user.id, user.email, !archived)}
        disabled={!!actionLoading[user.id]}
        title={archived ? 'Unarchive user (restores access)' : 'Archive user (prevents login, keeps history)'}
      >
        <span className="material-symbols-rounded">{archived ? 'unarchive' : 'archive'}</span>
      </button>
      {/* Disabled rather than hidden on an archived row, so the action set is
          the same shape on every row — see the table-actions rule in CLAUDE.md.
          A reset link would let an archived account back in, which is what
          archiving it was for. */}
      <button
        className={`btn-icon${archived ? ' btn-disabled' : ''}`}
        onClick={() => handleSendReset(user.id, user.email)}
        disabled={!!actionLoading[user.id] || archived}
        title={archived
          ? 'Restore the account before sending a reset link'
          : 'Email this user a link to set a new password'}
      >
        <span className="material-symbols-rounded">lock_reset</span>
      </button>
      <button
        className={`btn-icon btn-danger${user.is_admin ? ' btn-disabled' : ''}`}
        onClick={() => handleDelete(user.id, user.email)}
        disabled={!!actionLoading[user.id] || user.is_admin}
        title={user.is_admin ? 'Cannot delete an admin user' : 'Delete user'}
      >
        <span className="material-symbols-rounded">delete</span>
      </button>
    </td>
  );

  return (
    <div className="users-dashboard">
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirmDialog}
      />

      <div className="users-header">
        <h1>User Management</h1>
        <p className="users-subtitle">
          {activeTab === 'roles'
            ? 'Roles group permissions; users hold roles'
            : 'Accounts, the roles they hold, and their exceptions'}
        </p>
      </div>

      <div className="users-scroll">
        {activeTab === 'roles' ? (
          <div className="users-section">
            <RolesManager
              roles={roles}
              busyRoleId={busyRoleId}
              onCreate={() => setRoleEditor({ open: true, role: null })}
              onEdit={(role) => setRoleEditor({ open: true, role })}
              onDelete={handleDeleteRole}
            />
          </div>
        ) : (
          <>
            {pendingUsers.length > 0 && (
              <div className="users-section">
                <div className="section-header">
                  <h2>Pending Approvals ({pendingUsers.length})</h2>
                  <span className="status-badge">Action Required</span>
                </div>
                <div className="table-container">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>Name</th><th>Email</th><th>Requested</th>
                        <th className="actions-col">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingUsers.map(user => (
                        <tr key={user.id} className="pending-row">
                          <td><strong>{user.first_name} {user.last_name}</strong></td>
                          <td>{user.email}</td>
                          <td>{formatDate(user.created_at)}</td>
                          <td className="actions-col">
                            <button
                              className="btn-icon"
                              onClick={() => handleApprove(user.id, user.email)}
                              disabled={!!actionLoading[user.id]}
                              title="Approve user"
                            >
                              <span className="material-symbols-rounded">how_to_reg</span>
                            </button>
                            <button
                              className="btn-icon btn-danger"
                              onClick={() => handleDelete(user.id, user.email)}
                              disabled={!!actionLoading[user.id]}
                              title="Reject request"
                            >
                              <span className="material-symbols-rounded">close</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="users-section">
              <div className="section-header">
                <h2>Active Users ({activeUsers.length})</h2>
              </div>
              <div className="table-container">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Access</th>
                      <th>Rep</th>
                      <th>Approved</th>
                      <th>Xero</th>
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeUsers.map(user => (
                      <tr key={user.id}>
                        <td><strong>{user.first_name} {user.last_name}</strong></td>
                        <td>{user.email}</td>
                        <td className="access-cell">{renderAccessCell(user)}</td>
                        <td>{user.rep_name || <span className="muted">—</span>}</td>
                        <td>{formatDate(user.approved_at)}</td>
                        <td>{renderXeroCell(user)}</td>
                        {renderUserActions(user, { archived: false })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {archivedUsers.length > 0 && (
              <div className="users-section">
                <div className="section-header">
                  <h2>Archived Users ({archivedUsers.length})</h2>
                </div>
                <div className="table-container">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th><div className="centred">Access</div></th>
                        <th><div className="centred">Status</div></th>
                        <th className="actions-col">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {archivedUsers.map(user => (
                        <tr key={user.id} className="archived-row">
                          <td><strong>{user.first_name} {user.last_name}</strong></td>
                          <td>{user.email}</td>
                          <td className="access-cell">{renderAccessCell(user)}</td>
                          <td>
                            <div className="centred">
                              <span className="status-badge archived">Archived</span>
                            </div>
                          </td>
                          {renderUserActions(user, { archived: true })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <UserAccessModal
        isOpen={!!accessUser}
        user={accessUser}
        roles={roles}
        categories={categories}
        permissions={permissions}
        reps={reps}
        xero={xeroSummary}
        saving={savingAccess}
        onClose={() => setAccessUser(null)}
        onSave={saveAccess}
      />

      <RoleEditorModal
        isOpen={roleEditor.open}
        role={roleEditor.role}
        categories={categories}
        permissions={permissions}
        saving={savingRole}
        onClose={() => setRoleEditor({ open: false, role: null })}
        onSave={saveRole}
      />
    </div>
  );
}

export default UsersDashboard;
